import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, Brain, Lock, Check, ArrowRight, Clock, Shield, 
  Bot, Code, BookOpen, Layers, Variable, GitFork, Globe, Cpu, GraduationCap,
  Workflow, RefreshCw, Server, AlertTriangle, Search, Sparkles, Target,
  Users, Rocket, CheckCircle
} from 'lucide-react';
import BrandMark from '../components/BrandMark';
import { apiClient } from '../apiClient';
import { SKOOL_SIGNUP_URL } from '../config/externalLinks';

function LandingPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    founder_spots_remaining: null,
    founder_pricing_active: true
  });

  useEffect(() => {
    apiClient.get('/api/public/stats')
      .then(data => setStats(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      {/* Navigation */}
      <nav className="marketing-nav fixed top-0 w-full z-50 bg-gray-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="marketing-nav-inner max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <BrandMark size={36} />
              <span className="marketing-brand-title text-2xl font-black tracking-tight">REBEL PROMPTS</span>
            </div>
            <div className="marketing-nav-actions flex items-center gap-6">
              <button onClick={() => navigate('/login')} className="marketing-nav-link text-gray-400 hover:text-white font-medium transition-colors">
                Sign In
              </button>
              <a href={SKOOL_SIGNUP_URL} target="_blank" rel="noreferrer" className="btn-primary marketing-nav-cta">
                Get Started
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Lead with Pain */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-rebel-red/10 rounded-full filter blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-rebel-blue/10 rounded-full filter blur-[100px] pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {stats.founder_pricing_active && stats.founder_spots_remaining !== null && (
              <div className="inline-flex items-center gap-2 px-5 py-2 mb-8 rounded-full bg-white/5 border border-white/10">
                <Clock className="w-4 h-4 text-rebel-cyan" />
                <span className="text-sm font-medium text-gray-300">
                  Founder pricing: {stats.founder_spots_remaining.toLocaleString()} spots left
                </span>
              </div>
            )}
            
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black mb-8 leading-[1.1] tracking-tight">
              That perfect prompt you spent<br />
              <span className="text-gradient">3 hours crafting?</span>
            </h1>
            
            <p className="text-xl sm:text-3xl text-gray-300 mb-4 font-medium">
              It's gone.
            </p>
            <p className="text-lg sm:text-xl text-gray-500 mb-10 max-w-2xl mx-auto">
              Lost in a chat window you closed. Buried in a doc you'll never find. 
              Scattered across five different apps. Again.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={() => window.open(SKOOL_SIGNUP_URL, '_blank', 'noopener,noreferrer')}
                className="btn-primary landing-hero-cta text-lg px-10 py-4 flex items-center justify-center gap-2"
              >
                Stop Losing Your Work <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/library')}
                className="btn-secondary landing-hero-secondary text-lg px-10 py-4"
              >
                Browse the Library
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Join solopreneurs, developers, and AI builders who refuse to start from zero every time.
            </p>
          </div>
        </div>
      </section>

      {/* Pain Identification - "Sound Familiar?" */}
      <section className="py-20 border-y border-white/5 bg-gradient-to-b from-rebel-red/5 to-transparent">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-4">
            The AI power user's nightmare
          </h2>
          <p className="text-gray-500 text-center mb-12 text-lg">
            If you've felt any of these, you're in the right place.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-red-500/20 hover:border-red-500/40 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Search className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">"Where did I put that prompt?"</h3>
                  <p className="text-gray-400">You spend 20 minutes searching through ChatGPT history, Claude conversations, Notion pages, and random text files. The one that worked perfectly? Nowhere to be found.</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.02] border border-red-500/20 hover:border-red-500/40 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <RefreshCw className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">"I already solved this... last month"</h3>
                  <p className="text-gray-400">Recreating prompts you've already perfected. Writing the same system instructions from scratch. Wasting hours on problems you've already conquered.</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.02] border border-red-500/20 hover:border-red-500/40 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">"I need to deploy to fix a typo"</h3>
                  <p className="text-gray-400">Your AI agent's instructions are buried in code. Want to change "friendly" to "professional"? That's a git commit, PR review, and production deploy. For one word.</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.02] border border-red-500/20 hover:border-red-500/40 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Target className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">"Same prompt, completely different results"</h3>
                  <p className="text-gray-400">Your outputs are a lottery. Sometimes brilliant, sometimes garbage. No system, no consistency. Every session is a fresh roll of the dice.</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.02] border border-red-500/20 hover:border-red-500/40 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Layers className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">"Drowning in random AI info"</h3>
                  <p className="text-gray-400">YouTube videos, Twitter threads, Discord tips, Reddit posts. Endless information dumps with no structure. You're learning everything and mastering nothing.</p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.02] border border-red-500/20 hover:border-red-500/40 transition-all">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <Clock className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">"Figuring it out" is a full-time job</h3>
                  <p className="text-gray-400">Hours spent testing prompt variations. Days building automations from scratch. Weeks learning what someone else already perfected. The learning curve never ends.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-xl text-gray-400 mb-2">The real cost isn't just frustration.</p>
            <p className="text-2xl font-bold text-white">It's the <span className="text-red-400">hours every week</span> you'll never get back.</p>
          </div>
        </div>
      </section>

      {/* The Shift - Solution Intro */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rebel-cyan/10 border border-rebel-cyan/20 mb-6">
            <Sparkles className="w-4 h-4 text-rebel-cyan" />
            <span className="text-sm font-medium text-rebel-cyan">From "figuring it out" to "getting it done"</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl font-black mb-6">
            What if you had<br />
            <span className="text-gradient">a head start on everything?</span>
          </h2>
          
          <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto leading-relaxed">
            Imagine every prompt you've ever written—organized, searchable, versioned. Finally under control.
          </p>
          <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto leading-relaxed">
            Now imagine a <span className="text-white font-semibold">growing library</span> where the best prompts rise to the top. Templates you can fork. Workflows you can copy.
          </p>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
            <span className="text-white font-semibold">Courses that skip the fluff. A community building together.</span> The more people join, the more shortcuts everyone gets.
          </p>

          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm">
            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Find any prompt in seconds</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Fork proven templates</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Skip weeks of trial and error</span>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars Visual */}
      <section className="py-6 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-16 text-sm">
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-10 h-10 rounded-lg bg-rebel-red/20 flex items-center justify-center">
                <Brain className="w-5 h-5 text-rebel-red" />
              </div>
              <span className="font-medium">Prompt Library</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-10 h-10 rounded-lg bg-rebel-blue/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-rebel-blue" />
              </div>
              <span className="font-medium">Agent Instructions</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300">
              <div className="w-10 h-10 rounded-lg bg-rebel-cyan/20 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-rebel-cyan" />
              </div>
              <span className="font-medium">AI Courses</span>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 1: Prompt Library - Deep Dive */}
      <section className="py-24 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-rebel-red/20 flex items-center justify-center">
              <Brain className="w-6 h-6 text-rebel-red" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-black">Prompt Library</h2>
          </div>
          <p className="text-xl text-gray-400 text-center mb-6 max-w-2xl mx-auto">
            Your single source of truth. Never lose a prompt again.
          </p>
          <p className="text-center text-gray-500 mb-16 max-w-xl mx-auto">
            Stop the madness of scattered docs, lost chat histories, and recreating what you've already built.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Layers,
                title: 'Organize Like a Pro',
                description: 'Projects, folders, nested structures. Build a library that scales from 10 prompts to 10,000 without chaos. Find anything in seconds.'
              },
              {
                icon: Variable,
                title: 'Variables That Multiply',
                description: 'Add {{topic}}, {{audience}}, {{style}} and turn one prompt into hundreds of variations. Write once, use infinitely.'
              },
              {
                icon: RefreshCw,
                title: 'Version History',
                description: 'Every edit saved forever. Roll back anytime. See what changed and why. Iterate fearlessly—your old versions are always there.'
              },
              {
                icon: GitFork,
                title: 'Fork & Improve',
                description: 'See something good in the community library? Fork it, customize it, make it yours. Skip the blank page forever.'
              },
              {
                icon: Lock,
                title: 'Private by Default',
                description: 'Your prompts stay yours until you decide otherwise. Share with specific people, teams, or publish to the world. You control access.'
              },
              {
                icon: Globe,
                title: 'Access Everywhere',
                description: 'Web app, API, quick copy. Your prompts ready when you need them, however you work. No more "it\'s on my other computer."'
              }
            ].map((feature, i) => (
              <div 
                key={i} 
                className="p-8 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-rebel-red/30 transition-all duration-300"
              >
                <div className="w-14 h-14 mb-6 rounded-xl bg-gradient-to-br from-rebel-red/20 to-rebel-red/5 flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-rebel-red" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature 2: Agent Instructions - The Dev Pain Point */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-rebel-blue/20 flex items-center justify-center">
              <Bot className="w-6 h-6 text-rebel-blue" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-black">Agent Instructions</h2>
          </div>
          <p className="text-xl text-gray-400 text-center mb-4 max-w-2xl mx-auto">
            Stop hardcoding. Start shipping.
          </p>
          <p className="text-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rebel-blue/10 border border-rebel-blue/20 text-sm text-rebel-blue">
              <Cpu className="w-4 h-4" />
              Built for developers who ship AI products
            </span>
          </p>
          <p className="text-center text-gray-500 mb-16 max-w-xl mx-auto">
            Your AI agent's brain shouldn't live in your codebase. Update behavior in real-time. No deploys. No downtime. No risk.
          </p>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                icon: Server,
                title: 'Pull via API',
                description: 'One endpoint. Your agents fetch their instructions dynamically. Update once, every instance gets it immediately. Zero downtime.'
              },
              {
                icon: Code,
                title: 'Escape Deploy Hell',
                description: 'No more git commits for prompt tweaks. No more PR reviews for tone changes. Edit in Rebel Prompts, your bot adapts in real time.'
              },
              {
                icon: Workflow,
                title: 'Dev & Live Environments',
                description: 'Test risky changes in dev. Push to live when ready. A/B test different instructions. Roll back if something breaks. Real version control for AI.'
              },
              {
                icon: Variable,
                title: 'Runtime Variables',
                description: 'Pass context dynamically. {{user_name}}, {{current_date}}, {{account_type}}. Same instruction, personalized every single time.'
              }
            ].map((feature, i) => (
              <div 
                key={i} 
                className="p-8 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-rebel-blue/30 transition-all duration-300"
              >
                <div className="w-14 h-14 mb-6 rounded-xl bg-gradient-to-br from-rebel-blue/20 to-rebel-blue/5 flex items-center justify-center">
                  <feature.icon className="w-7 h-7 text-rebel-blue" />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Code Example */}
          <div className="mt-12 max-w-2xl mx-auto">
            <p className="text-center text-sm text-gray-500 mb-4">It's this simple:</p>
            <div className="rounded-xl bg-gray-900/80 border border-white/10 overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                <span className="ml-2 text-xs text-gray-500">your-agent.py</span>
              </div>
              <pre className="p-4 text-sm overflow-x-auto">
                <code className="text-gray-300">
{`# Your agent pulls fresh instructions every time
instructions = requests.get(
    "https://api.rebelprompts.com/v1/agent/instructions/my-bot",
    headers={"X-API-Key": API_KEY},
    params={"env": "live", "user_name": user.name}
).json()["instruction"]

# Update in Rebel Prompts → Instant change. No deploy.`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 3: Courses */}
      <section className="py-24 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-rebel-cyan/20 flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-rebel-cyan" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-black">AI Courses</h2>
          </div>
          <p className="text-xl text-gray-400 text-center mb-4 max-w-2xl mx-auto">
            Stop drowning in random YouTube tutorials.
          </p>
          <p className="text-center text-gray-500 mb-16 max-w-xl mx-auto">
            Structured courses built by practitioners who ship AI products daily. No fluff. No filler. Just the workflows that actually work.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-rebel-cyan/30 transition-all">
              <BookOpen className="w-8 h-8 text-rebel-cyan mb-4" />
              <h3 className="text-lg font-bold mb-2">Prompt Engineering</h3>
              <p className="text-gray-400 text-sm">Write prompts that work every time. Skip months of trial and error with proven techniques for consistency and complex tasks.</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-rebel-cyan/30 transition-all">
              <Bot className="w-8 h-8 text-rebel-cyan mb-4" />
              <h3 className="text-lg font-bold mb-2">Building AI Agents</h3>
              <p className="text-gray-400 text-sm">Build agents that actually work. Instructions, tool use, memory systems. Go from concept to production without the guesswork.</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-rebel-cyan/30 transition-all">
              <Workflow className="w-8 h-8 text-rebel-cyan mb-4" />
              <h3 className="text-lg font-bold mb-2">AI Automation</h3>
              <p className="text-gray-400 text-sm">Plug-and-play workflow builds. n8n, Make, Zapier, custom scripts. Copy what works instead of building from scratch.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Templates Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Users className="w-4 h-4 text-white" />
              <span className="text-sm font-medium text-gray-300">Build Together, Win Together</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-black mb-4">
              Your library grows.<br />
              <span className="text-gradient">So does everyone else's.</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Every prompt you share, every template you publish—they help others skip the struggle you went through. And when they share, you benefit too.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 text-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rebel-red/20 to-rebel-red/5 flex items-center justify-center mx-auto mb-4">
                <Brain className="w-7 h-7 text-rebel-red" />
              </div>
              <h3 className="text-lg font-bold mb-2">Prompt Templates</h3>
              <p className="text-gray-500 text-sm">Share what works. Fork what others built. One-click to make any public prompt yours to customize.</p>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 text-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rebel-blue/20 to-rebel-blue/5 flex items-center justify-center mx-auto mb-4">
                <Workflow className="w-7 h-7 text-rebel-blue" />
              </div>
              <h3 className="text-lg font-bold mb-2">Workflow Builds</h3>
              <p className="text-gray-500 text-sm">Automation templates that actually work. Copy the setup, skip the debugging.</p>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 text-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rebel-cyan/20 to-rebel-cyan/5 flex items-center justify-center mx-auto mb-4">
                <Layers className="w-7 h-7 text-rebel-cyan" />
              </div>
              <h3 className="text-lg font-bold mb-2">Organized Collections</h3>
              <p className="text-gray-500 text-sm">Curate and share folders by use case. Marketing, coding, content—structured how it makes sense.</p>
            </div>

            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 text-center">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-green-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Growing Community</h3>
              <p className="text-gray-500 text-sm">Early members shape what gets built. The more people join, the more valuable it gets for everyone.</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-400">
              <span className="text-white font-semibold">Network effects are real:</span> every member who shares makes the whole library stronger.
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof / Who It's For */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-12">
            Built for people who <span className="text-gradient">build with AI</span>
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Rocket, title: 'Solopreneurs', desc: 'Work like a team of 10. Your AI arsenal, always ready.' },
              { icon: Code, title: 'Vibe Coders', desc: 'Ship AI features fearlessly. Iterate without deploys.' },
              { icon: Users, title: 'Startups', desc: 'Shared prompt intelligence. Compound as a team.' },
              { icon: Sparkles, title: 'Creators', desc: 'Consistent quality, every time. Scale your content.' }
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/[0.02] border border-white/5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            {stats.founder_pricing_active && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rebel-cyan/10 border border-rebel-cyan/20 mb-6">
                <Zap className="w-4 h-4 text-rebel-cyan" />
                <span className="text-sm font-medium text-rebel-cyan">Founder Pricing — Lock It In Forever</span>
              </div>
            )}
            <h2 className="text-4xl sm:text-5xl font-black mb-4">
              Stop losing time.<br />Start building your system.
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {stats.founder_pricing_active 
                ? `First 1,000 users get these prices forever. ${stats.founder_spots_remaining !== null ? `Only ${stats.founder_spots_remaining.toLocaleString()} spots left.` : ''}`
                : 'Join thousands of AI power users building with Rebel Prompts.'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Starter */}
            <div className="p-8 rounded-2xl bg-white/[0.03] border border-white/10">
              <h3 className="text-2xl font-bold mb-2">Starter</h3>
              <p className="text-gray-400 mb-6">For individuals building their AI toolkit.</p>
              
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-gray-500 line-through text-lg">$27</span>
                <span className="text-5xl font-black">$17</span>
                <span className="text-gray-400">/mo</span>
              </div>
              <p className="text-sm text-rebel-cyan mb-8">Lock in $120/year savings forever</p>

              <ul className="space-y-3 mb-8">
                {[
                  'Unlimited prompts & folders',
                  'Variable templates',
                  'Full version history',
                  'Community library access',
                  'Fork any public prompt',
                  '20 AI credits/month',
                  'Email support'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-rebel-cyan flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <button onClick={() => window.open(SKOOL_SIGNUP_URL, '_blank', 'noopener,noreferrer')} className="btn-secondary w-full">
                Get Starter
              </button>
            </div>

            {/* Pro */}
            <div className="p-8 rounded-2xl bg-gradient-to-b from-rebel-cyan/10 to-transparent border border-rebel-cyan/30 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 rounded-full bg-rebel-cyan text-black text-sm font-semibold">
                  Most Popular
                </span>
              </div>

              <h3 className="text-2xl font-bold mb-2">Pro</h3>
              <p className="text-gray-400 mb-6">For power users and teams who ship.</p>
              
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-gray-500 line-through text-lg">$47</span>
                <span className="text-5xl font-black">$27</span>
                <span className="text-gray-400">/mo</span>
              </div>
              <p className="text-sm text-rebel-cyan mb-8">Lock in $240/year savings forever</p>

              <ul className="space-y-3 mb-8">
                {[
                  'Everything in Starter',
                  'Agent Instructions API',
                  'Dev & Live environments',
                  '55 AI credits/month',
                  'AI prompt generation',
                  'Team collaboration',
                  'Priority support'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-rebel-cyan flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <button onClick={() => window.open(SKOOL_SIGNUP_URL, '_blank', 'noopener,noreferrer')} className="btn-primary w-full flex items-center justify-center gap-2">
                Get Pro <Zap className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Secure via Stripe</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                <span>Instant access</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-black mb-6">
            Stop figuring it out.<br />
            <span className="text-gradient">Start getting it done.</span>
          </h2>
          <p className="text-xl text-gray-400 mb-4 max-w-2xl mx-auto">
            Your prompts, finally organized. A system that grows with you. A community building together.
          </p>
          <p className="text-lg text-gray-500 mb-10">
            Join as a founder member. Lock in early pricing forever. Help shape what gets built next.
          </p>
          <button
            onClick={() => window.open(SKOOL_SIGNUP_URL, '_blank', 'noopener,noreferrer')}
            className="btn-primary landing-final-cta text-xl px-12 py-5 flex items-center gap-3 mx-auto"
          >
            Get Started Now <ArrowRight className="w-5 h-5" />
          </button>
          {stats.founder_pricing_active && stats.founder_spots_remaining !== null && (
            <p className="text-sm text-gray-500 mt-6">
              {stats.founder_spots_remaining.toLocaleString()} founder spots remaining
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <BrandMark size={24} />
              <span className="font-bold">REBEL PROMPTS</span>
            </div>
            <div className="flex gap-8 text-sm text-gray-500">
              <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button>
              <button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button>
              <button onClick={() => navigate('/docs')} className="hover:text-white transition-colors">Docs</button>
              <button onClick={() => navigate('/support')} className="hover:text-white transition-colors">Support</button>
            </div>
            <div className="text-sm text-gray-600">
              © 2026 Rebel Prompts
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
